// #501 — a member with an open verification is held on /os-meus-dados until
// they have answered it (decision D7: blocking). The hold lifts at `answered`,
// not `applied`: once the member has done their part, an admin's review does
// not keep them out.
//
// Front-end only, like every route guard in this app (router meta is cosmetic;
// the API stays open) — what it protects is the member's attention, not data.
// Admins and editors are never held, even when they are also members: the check
// is on the real role, not the member view mode.

export const VERIFICATION_ROUTE = 'memberVerification'

// Where a held member may still go: getting in and out, setting a password
// after a login link, and the legal pages linked from the footer.
export const ALWAYS_ALLOWED = new Set([
  VERIFICATION_ROUTE,
  'home',
  'login',
  'logout',
  'forgot',
  'recover',
  'createAccountByEmail',
  'usersEditPassword',
  'usersEditPasswordNoId',
  'privacyPolicy',
  'dataProcessingAgreement',
])

/**
 * @param {{ role: () => string|undefined, memberId: () => string|undefined,
 *           fetchMine: () => Promise<object|null> }} deps
 */
export function createVerificationGate({ role, memberId, fetchMine }) {
  // One lookup per member per session; invalidated when the member answers.
  let cached = { memberId: null, verification: null }

  const lookup = (id) => {
    if (cached.memberId !== id) {
      cached = { memberId: id, verification: fetchMine().catch(() => null) }
    }
    return cached.verification
  }

  const guard = async (to) => {
    if (ALWAYS_ALLOWED.has(to.name)) return true
    const id = memberId()
    if (role() !== 'member' || !id) return true
    const v = await lookup(id)
    if (v && v.status === 'pending') return { name: VERIFICATION_ROUTE }
    return true
  }

  return {
    guard,
    invalidate() {
      cached = { memberId: null, verification: null }
    },
  }
}
